import {
  Box,
  Button,
  ExpandableSection,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  Toggle,
} from "@amzn/awsui-components-console";
import React, { useState } from "react";

import { EvalCase, EvalCaseFormData } from "@/hooks/admin/useAdminEvalDataset";

interface EvalCaseModalProps {
  visible: boolean;
  mode: "add" | "edit";
  evalCase: EvalCase | null;
  saving: boolean;
  onDismiss: () => void;
  onSave: (caseId: string, data: EvalCaseFormData) => Promise<boolean>;
}

const DIFFICULTY_OPTIONS = [
  { value: "simple", label: "Simple", description: "Straightforward single-stat lookups" },
  { value: "moderate", label: "Moderate", description: "Joins, filters, or comparisons" },
  { value: "complex", label: "Complex", description: "Multi-step logic, CTEs, or aggregations" },
];

/** Generate a slug from question text for auto-suggesting case IDs. */
const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 50);

const EvalCaseModal: React.FC<EvalCaseModalProps> = ({
  visible,
  mode,
  evalCase,
  saving,
  onDismiss,
  onSave,
}) => {
  // Form state
  const [caseId, setCaseId] = useState(evalCase?.case_id ?? "");
  const [caseIdTouched, setCaseIdTouched] = useState(mode === "edit");
  const [turns, setTurns] = useState<string[]>(
    evalCase?.turns?.length ? [...evalCase.turns] : [""]
  );
  const [expectedSql, setExpectedSql] = useState<string[]>(
    evalCase?.expected_sql?.length ? [...evalCase.expected_sql] : [""]
  );
  const [expectedAnswer, setExpectedAnswer] = useState(
    evalCase?.expected_answer ?? ""
  );
  const [difficulty, setDifficulty] = useState<string>(
    evalCase?.difficulty ?? "simple"
  );
  const [tags] = useState<string[]>(evalCase?.tags ?? ["smoke", "full", "real_dataset"]);
  const [disabled, setDisabled] = useState(evalCase?.disabled ?? false);
  const [outOfScope, setOutOfScope] = useState(evalCase?.out_of_scope ?? false);
  const [allowNonDeterministic, setAllowNonDeterministic] = useState(
    evalCase?.allow_non_deterministic ?? true
  );
  const [scoreAllTurns, setScoreAllTurns] = useState(
    evalCase?.score_all_turns ?? false
  );
  const [knownFlaky, setKnownFlaky] = useState(evalCase?.known_flaky ?? false);
  const [sourceTab, setSourceTab] = useState(evalCase?.source_tab ?? "");

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!caseId.trim()) {
      newErrors.caseId = "Test ID is required.";
    } else if (!/^[a-z0-9_]+$/.test(caseId)) {
      newErrors.caseId =
        "Test ID must contain only lowercase letters, numbers, and underscores.";
    }
    const nonEmptyTurns = turns.filter((t) => t.trim());
    if (nonEmptyTurns.length === 0) {
      newErrors.turns = "At least one question is required.";
    }
    if (!expectedAnswer.trim()) {
      newErrors.expectedAnswer = "Expected answer is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const data: EvalCaseFormData = {
      tags,
      turns: turns.filter((t) => t.trim()),
      expected_sql: expectedSql.filter((s) => s.trim()),
      expected_answer: expectedAnswer.trim(),
      difficulty: difficulty as "simple" | "moderate" | "complex",
      disabled,
      out_of_scope: outOfScope,
      allow_non_deterministic: allowNonDeterministic,
      score_all_turns: scoreAllTurns,
      known_flaky: knownFlaky,
      source_tab: sourceTab.trim() || null,
    };

    await onSave(caseId.trim(), data);
  };

  // --- Auto-suggest case ID from first question ---
  const handleTurnChange = (index: number, value: string) => {
    const updated = [...turns];
    updated[index] = value;
    setTurns(updated);

    // Auto-generate case ID from first question if user hasn't manually edited it
    if (index === 0 && mode === "add" && !caseIdTouched) {
      setCaseId(slugify(value));
    }
  };

  const addTurn = () => setTurns([...turns, ""]);

  const removeTurn = (index: number) => {
    if (turns.length <= 1) return;
    setTurns(turns.filter((_, i) => i !== index));
  };

  const updateSql = (index: number, value: string) => {
    const updated = [...expectedSql];
    updated[index] = value;
    setExpectedSql(updated);
  };

  const addSql = () => setExpectedSql([...expectedSql, ""]);

  const removeSql = (index: number) => {
    if (expectedSql.length <= 1) return;
    setExpectedSql(expectedSql.filter((_, i) => i !== index));
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={mode === "add" ? "Add test case" : `Edit: ${evalCase?.case_id}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {/* Questions — the primary field */}
        <FormField
          label="Questions"
          description="What should the user ask? For multi-turn conversations, add follow-up questions in order."
          errorText={errors.turns}
        >
          <SpaceBetween size="s">
            {turns.map((turn, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <Textarea
                    value={turn}
                    onChange={({ detail }) => handleTurnChange(i, detail.value)}
                    placeholder={i === 0 ? "e.g. Who led the NBA in assists in 2024-25?" : `Follow-up question ${i + 1}`}
                    rows={2}
                  />
                </div>
                {turns.length > 1 && (
                  <Button
                    variant="icon"
                    iconName="remove"
                    ariaLabel={`Remove question ${i + 1}`}
                    onClick={() => removeTurn(i)}
                  />
                )}
              </div>
            ))}
            <Button iconName="add-plus" onClick={addTurn}>
              Add follow-up question
            </Button>
          </SpaceBetween>
        </FormField>

        {/* Expected Answer */}
        <FormField
          label="Expected Answer"
          description="What should the agent's response contain? This is used to score the answer."
          errorText={errors.expectedAnswer}
        >
          <Textarea
            value={expectedAnswer}
            onChange={({ detail }) => setExpectedAnswer(detail.value)}
            placeholder="e.g. Trae Young led the league in total assists for the 2024-25 regular season."
            rows={3}
          />
        </FormField>

        {/* Difficulty */}
        <FormField
          label="Difficulty"
          description="Used for grouping in the evaluation report and pass/fail thresholds. Does not affect how the case is scored."
        >
          <Select
            selectedOption={
              DIFFICULTY_OPTIONS.find((o) => o.value === difficulty) ?? null
            }
            onChange={({ detail }) =>
              setDifficulty(detail.selectedOption.value ?? "simple")
            }
            options={DIFFICULTY_OPTIONS}
          />
        </FormField>

        {/* Test ID */}
        <FormField
          label="Test ID"
          description="Auto-generated from the question. You can override this if needed."
          errorText={errors.caseId}
        >
          <Input
            value={caseId}
            onChange={({ detail }) => {
              setCaseId(detail.value);
              setCaseIdTouched(true);
            }}
            placeholder="auto_generated_from_question"
            disabled={mode === "edit"}
          />
        </FormField>

        {/* Out of Scope toggle — visible in primary section */}
        <Toggle
          checked={outOfScope}
          onChange={({ detail }) => setOutOfScope(detail.checked)}
          description="The agent should refuse to answer this question (e.g., non-sports topic used to test guardrails)."
        >
          Out of scope
        </Toggle>

        {/* Disabled toggle */}
        <Toggle
          checked={disabled}
          onChange={({ detail }) => setDisabled(detail.checked)}
          description="Temporarily exclude this case from evaluation runs without deleting it."
        >
          Disabled
        </Toggle>

        {/* --- Advanced Settings --- */}
        <ExpandableSection headerText="Advanced settings" variant="footer">
          <SpaceBetween size="l">
            {/* Expected SQL */}
            <FormField
              label="Expected Query (SQL)"
              description="Optional. If provided, the eval scores whether the agent generates correct SQL. If left blank, the case is only scored on answer quality (SQL accuracy is auto-passed)."
            >
              <SpaceBetween size="s">
                {expectedSql.map((sql, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <Textarea
                        value={sql}
                        onChange={({ detail }) => updateSql(i, detail.value)}
                        placeholder={`SQL alternative ${i + 1} (optional)`}
                        rows={6}
                        spellcheck={false}
                      />
                    </div>
                    {expectedSql.length > 1 && (
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel={`Remove SQL alternative ${i + 1}`}
                        onClick={() => removeSql(i)}
                      />
                    )}
                  </div>
                ))}
                <Button iconName="add-plus" onClick={addSql}>
                  Add SQL alternative
                </Button>
              </SpaceBetween>
            </FormField>

            {/* Scoring toggles */}
            <FormField label="Scoring options">
              <SpaceBetween size="s">
                <Toggle
                  checked={allowNonDeterministic}
                  onChange={({ detail }) => setAllowNonDeterministic(detail.checked)}
                  description="Allow the agent's answer to vary in wording as long as the facts are correct."
                >
                  Flexible scoring
                </Toggle>
                <Toggle
                  checked={scoreAllTurns}
                  onChange={({ detail }) => setScoreAllTurns(detail.checked)}
                  description="For multi-question conversations, score each response individually."
                >
                  Score every question
                </Toggle>
                <Toggle
                  checked={knownFlaky}
                  onChange={({ detail }) => setKnownFlaky(detail.checked)}
                  description="This test sometimes passes and sometimes fails due to external factors. It won't block a passing evaluation."
                >
                  Unstable (excluded from pass/fail)
                </Toggle>
              </SpaceBetween>
            </FormField>

            {/* Source/Origin */}
            <FormField label="Origin" description="Optional label for where this case came from.">
              <Input
                value={sourceTab}
                onChange={({ detail }) => setSourceTab(detail.value)}
                placeholder="e.g. user_feedback, qa_review"
              />
            </FormField>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Modal>
  );
};

export default EvalCaseModal;
